import React, { useState, useEffect } from 'react'
import axios from "axios";
import { APIURL, TZ } from "../../../Global";
import { Modal } from "react-bootstrap";
import './bankinginfo.css';
import { useParams } from 'react-router-dom';
import WasabiImage from './WasabiImage';
import s3 from './wasabi';
import { useTranslation } from 'react-i18next';

const BankingInfo = () => {
  const { t } = useTranslation();
  const [showPreview, setShowPreview] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const { id } = useParams();
  const [bankInfo, setBankInfo] = useState({});
  const [allInfo, setAllInfo] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchBankDetails = async () => {
      setLoading(true);
      try {
        const tokenString = sessionStorage.getItem("usertoken");
        const token = tokenString.replace(/["]+/g, "");
        const response = await axios.get(`${APIURL}/api/v1/staff/vendor-bank-detail/?hospital_id=${id}&${TZ}`, {
          headers: { Authorization: "Token " + token },
        });

        if (response.data.status === 'success') {
          if (response.data.message) {
            setError(t('bankingInfo.noBankInfo'));
          } else {
            setBankInfo(response.data.hospital);
            setAllInfo(response.data);
          }
        }
      } catch (error) {
        console.error('Error fetching bank details:', error);
        setError(t('bankingInfo.errorLoading') || 'Error loading banking information');
      } finally {
        setLoading(false);
      }
    };

    fetchBankDetails();
  }, [id, t]);

  useEffect(() => {
    const fetchPdfUrl = async () => {
      if (!selectedFile || !isPdfFile(selectedFile.file_name)) {
        setPdfUrl(null);
        return;
      }

      setLoadingPdf(true);
      try {
        const cleanFileType = (selectedFile.file_type || '').replace(/^\/+|\/+$/g, '');
        const cleanUserOrFolder = (allInfo.path || '').replace(/^\/+|\/+$/g, '');
        const key = `${cleanUserOrFolder}/${id}/${cleanFileType}/${selectedFile.file_name}`;

        const url = await s3.getSignedUrlPromise('getObject', {
          Bucket: allInfo.bucket_name,
          Key: key,
          Expires: 3600,
          ResponseContentDisposition: 'inline',
          ResponseContentType: 'application/pdf'
        });

        setPdfUrl(url);
      } catch (err) {
        console.error('Error fetching PDF URL:', err);
        setError('Failed to load PDF');
      } finally {
        setLoadingPdf(false);
      }
    };

    if (selectedFile && showPreview) {
      fetchPdfUrl();
    }
  }, [selectedFile, showPreview, allInfo.bucket_name, allInfo.path, id]);

  const displayValue = (value, capitalize = false) => {
    if (!value || (typeof value === 'string' && value.trim() === '')) {
      return <span style={{ opacity: 0.6 }}>{t('bankingInfo.notProvided')}</span>;
    }
    return <span className={capitalize ? 'capitalize' : ''}>{value}</span>;
  };

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.25, 0.5));
  };

  const handleZoomReset = () => {
    setZoomLevel(1);
  };

  const handleFileClick = (file) => {
    setSelectedFile(file);
    setShowPreview(true);
    setZoomLevel(1);
    setPdfUrl(null);
  };

  const handleClosePreview = () => {
    setShowPreview(false);
    setSelectedFile(null);
    setZoomLevel(1);
    setPdfUrl(null);
  };

  const getFileIcon = (fileType) => {
    const type = fileType?.toLowerCase();
    if (type === 'pdf') {
      return <i className="fas fa-file-pdf banking-file-icon banking-pdf-icon"></i>;
    } else if (['jpg', 'jpeg', 'png', 'gif'].includes(type)) {
      return <i className="fas fa-file-image banking-file-icon banking-image-icon"></i>;
    }
    return <i className="fas fa-file banking-file-icon banking-default-icon"></i>;
  };

  const isImageFile = (fileName) => {
    const extension = fileName?.split('.').pop()?.toLowerCase();
    return ['jpg', 'jpeg', 'png', 'gif'].includes(extension);
  };

  const isPdfFile = (fileName) => {
    const extension = fileName?.split('.').pop()?.toLowerCase();
    return extension === 'pdf';
  };

  const renderFileContent = () => {
    if (!selectedFile) return null;

    if (isPdfFile(selectedFile.file_name)) {
      return (
        <div className="banking-pdf-preview-container">
          {loadingPdf ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh' }}>
              <div className="banking-spinner"></div>
              <p style={{ marginLeft: '10px' }}>{t('bankingInfo.loading')}</p>
            </div>
          ) : pdfUrl ? (
            <embed
              src={pdfUrl}
              type="application/pdf"
              width="100%"
              height="100%"
              style={{ minHeight: '70vh', border: 'none' }}
              title={selectedFile.file_name}
            />
          ) : (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh' }}>
              <p>Failed to load PDF</p>
            </div>
          )}
        </div>
      );
    }
    else if (isImageFile(selectedFile.file_name)) {
      return (
        <div className="banking-image-preview-container">
          <div className="banking-zoom-controls">
            <button
              className="banking-zoom-btn"
              onClick={handleZoomOut}
              title={t('bankingInfo.zoomOut')}
              disabled={zoomLevel <= 0.5}
            >
              <i className="fas fa-minus"></i>
            </button>
            <div className="banking-zoom-level">{Math.round(zoomLevel * 100)}%</div>
            <button
              className="banking-zoom-btn"
              onClick={handleZoomIn}
              title={t('bankingInfo.zoomIn')}
              disabled={zoomLevel >= 3}
            >
              <i className="fas fa-plus"></i>
            </button>
            <button
              className="banking-zoom-btn"
              onClick={handleZoomReset}
              title={t('bankingInfo.resetZoom')}
            >
              <i className="fas fa-undo"></i>
            </button>
          </div>
          <div className="banking-image-scroll-area">
            <div className="banking-image-content-wrapper">
              <div
                className="banking-image-transform-wrapper"
                style={{
                  transform: `scale(${zoomLevel})`
                }}
              >
                <WasabiImage
                  bucketName={allInfo.bucket_name}
                  user_or_folder={allInfo.path}
                  hospital_id={id}
                  file_type={selectedFile.file_type}
                  fileName={selectedFile.file_name}
                  from="bankingInfo"
                />
              </div>
            </div>
          </div>
        </div>
      );
    }
    else {
      return (
        <div className="banking-unsupported-file-container">
          <i className="fas fa-file fa-4x banking-default-icon" style={{ marginBottom: '20px' }}></i>
          <p style={{ fontSize: '18px', marginBottom: '20px' }}>
            {t('bankingInfo.cannotPreviewFile')}
          </p>
        </div>
      );
    }
  };

  if (loading) {
    return (
      <div className="banking-loading-state">
        <div className="banking-spinner"></div>
        <p>{t('bankingInfo.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="banking-error-state">
        <i className="fa fa-exclamation-circle"></i>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="banking-preview-container">
      <h2 className="banking-section-title">{t('bankingInfo.title')}</h2>

      <div className="banking-details-card">
        <div className="banking-details-grid">
          <div className="banking-grid-item">
            <p className="banking-field-label">{t('bankingInfo.accountHolder')}</p>
            <p className="banking-field-value">{displayValue(bankInfo.account_name, true)}</p>
          </div>
          <div className="banking-grid-item">
            <p className="banking-field-label">{t('bankingInfo.bankName')}</p>
            <p className="banking-field-value">{displayValue(bankInfo.bank_name, true)}</p>
          </div>
          <div className="banking-grid-item">
            <p className="banking-field-label">{t('bankingInfo.accountNumber')}</p>
            <p className="banking-field-value">{displayValue(bankInfo.iban)}</p>
          </div>
          <div className="banking-grid-item">
            <p className="banking-field-label">{t('bankingInfo.swiftCode')}</p>
            <p className="banking-field-value">{displayValue(bankInfo.bic_code)}</p>
          </div>
        </div>

        <div className="banking-details-grid">
          <div className="banking-grid-item">
            <p className="banking-field-label">{t('bankingInfo.paymentMethod')}</p>
            <p className="banking-field-value">
              {bankInfo.payment_method
                ? t(`paymentMethods.${bankInfo.payment_method}`)
                : <span style={{ opacity: 0.6 }}>{t('bankingInfo.notProvided')}</span>
              }
            </p>
          </div>
          <div className="banking-grid-item">
            <p className="banking-field-label">{t('bankingInfo.currency')}</p>
            <p className="banking-field-value">
              {displayValue(bankInfo.currency?.toUpperCase())}
            </p>
          </div>
          <div className="banking-grid-item">
            <p className="banking-field-label">{t('bankingInfo.branchAddress')}</p>
            <p className="banking-field-value">{displayValue(bankInfo.address, true)}</p>
          </div>
          <div className="banking-grid-item">
            <p className="banking-field-label">{t('bankingInfo.subscriptionPlan')}</p>
            <p className="banking-field-value">
              {allInfo.hospital_plan ? (
                <span>
                  {t(`bankingInfo.${allInfo.hospital_plan.version_name.toLowerCase()}`)}
                  ({t(`bankingInfo.${allInfo.hospital_plan.plan_period.toLowerCase()}`)})
                </span>
              ) : (
                <span style={{ opacity: 0.6 }}>{t('bankingInfo.notProvided')}</span>
              )}
            </p>
          </div>
        </div>

        <div className="banking-details-grid">
          <div className="banking-grid-item banking-full-width">
            <p className="banking-field-label">{t('bankingInfo.proofDocument')}</p>
            {bankInfo.account_proof?.length > 0 ? (
              <div className="banking-file-preview-list">
                {bankInfo.account_proof.map((file, index) => (
                  <div
                    key={index}
                    className="banking-file-preview-thumbnail banking-clickable-file"
                    onClick={() => handleFileClick(file)}
                    title={`Click to preview: ${file.file_name}`}
                  >
                    {getFileIcon(file.file_type)}
                    <span className="banking-file-preview-filename">
                      {file.file_name}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="banking-field-value">
                <span style={{ opacity: 0.6 }}>{t('bankingInfo.notProvided')}</span>
              </p>
            )}
          </div>
        </div>
      </div>

      <Modal
        show={showPreview}
        onHide={handleClosePreview}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        backdrop="static"
        keyboard={false}
        className="banking-file-preview-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-file me-2"></i>
            {selectedFile?.file_name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '0', minHeight: '400px' }}>
          {renderFileContent()}
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex justify-content-between w-100">
            <button
              style={{ marginLeft: "auto", backgroundColor: "#65c7c2", borderColor: "#65c7c2" }}
              className="btn btn-primary btn-sm"
              onClick={handleClosePreview}
            >
              {t('bankingInfo.close')}
            </button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default BankingInfo;